import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Loader2, AlertCircle, Mic, MicOff, Eye, User, Plus, ArrowUp } from 'lucide-react';
import useSound from '../hooks/useSound';
import { useImagePreloader, getCachedUrl } from '../hooks/useImagePreloader';
import { api } from '../services/api';
import { getShortTitle } from '../utils/text';
import { IOS_SPRING } from '../constants';
import { extractSessionPhotos as extractPhotos } from '../utils/session';


// ── Derive granular status label from task-level info ──
function getGranularStatus(session) {
    const task = session._activeTask;

    if (task?.queuePosition > 0) {
        return { label: `Waiting... (${task.queuePosition} ahead)`, color: 'rgba(0,0,0,0.4)', pulse: false };
    }
    if (task?.mayBeStuck) {
        return { label: 'May be stuck', color: '#CA8A04', pulse: false };
    }

    const taskStatus = task?.status;
    switch (taskStatus) {
        case 'loading':
        case 'streaming':
            return { label: 'Running...', color: '#000', pulse: true };
        case 'done':
            return { label: 'Finished', color: '#16a34a', pulse: false };
        case 'error':
            return { label: 'Failed', color: '#DC2626', pulse: false };
        case 'stopped':
            return { label: 'Stopped', color: '#EA580C', pulse: false };
        case 'timeout':
            return { label: 'Timed out', color: '#DC2626', pulse: false };
        default:
            break;
    }

    // Fallback to session-level status
    if (session.status === 'pending') return { label: 'Queued', color: 'rgba(0,0,0,0.4)', pulse: false };
    return { label: 'Processing', color: '#000', pulse: true };
}

// ── Active session card — iOS light ──
function ActiveSessionCard({ session, onClick, onDismiss, onRetry, onStop, formatDate }) {
    const photos = extractPhotos(session);
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 15000);
        return () => clearInterval(timer);
    }, []);

    const elapsed = session.created_at ? now - new Date(session.created_at).getTime() : 0;
    const isStale = elapsed > 600000;

    const { label: statusLabel, color: statusColor, pulse: statusPulse } = getGranularStatus(session);

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={IOS_SPRING}
            onClick={onClick}
            className="cursor-pointer active:scale-[0.98] transition-transform"
            style={{
                background: '#fff',
                borderRadius: 24,
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04)',
            }}
        >
            {/* Hero photo */}
            {photos.length > 0 && (
                <div className="relative" style={{ height: 120 }}>
                    <img
                        src={photos[0]?.url || photos[0]}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ opacity: 0.85 }}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                        }}
                    />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 40%, rgba(255,255,255,0.95) 100%)' }} />
                </div>
            )}

            <div className="px-5 py-4">
                {/* Status pill */}
                <div className="flex items-center gap-2 mb-2">
                    <motion.div
                        className="w-2 h-2 rounded-full"
                        style={{ background: statusColor }}
                        animate={statusPulse ? { opacity: [1, 0.3, 1] } : { opacity: 1 }}
                        transition={statusPulse ? { duration: 1.5, repeat: Infinity, ease: 'easeInOut' } : undefined}
                    />
                    <span
                        className="font-semibold tracking-wide uppercase"
                        style={{ fontSize: 10, color: statusColor, letterSpacing: '0.08em' }}
                    >
                        {statusLabel}
                    </span>
                </div>

                <p className="font-semibold leading-snug line-clamp-2" style={{ fontSize: 15, color: '#000' }}>
                    {getShortTitle(session.prompt) || 'Working on your request…'}
                </p>

                <div className="flex items-center justify-between mt-3">
                    <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.3)' }}>
                        {formatDate(session.created_at)}
                    </span>
                </div>

                {/* Progress bar */}
                {session.status === 'progress' && (
                    <div className="mt-3 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.06)' }}>
                        <motion.div
                            className="h-full rounded-full"
                            style={{ background: '#000' }}
                            initial={{ width: '0%' }}
                            animate={{ width: ['10%', '60%', '30%', '80%', '45%'] }}
                            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                        />
                    </div>
                )}

                {/* Timeout — retry action */}
                {session._activeTask?.status === 'timeout' && (
                    <div className="mt-3 flex items-center justify-between gap-2 px-3 py-2 rounded-2xl"
                        style={{ background: 'rgba(220,38,38,0.04)', border: '1px solid rgba(220,38,38,0.12)' }}>
                        <span style={{ fontSize: 11, color: '#DC2626' }}>Task timed out</span>
                        <button
                            onClick={(e) => { e.stopPropagation(); onRetry?.(session); }}
                            className="font-medium px-2.5 py-0.5 rounded-full active:scale-95 transition-transform"
                            style={{ fontSize: 11, color: '#fff', background: '#DC2626' }}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* May be stuck — stop action */}
                {session._activeTask?.mayBeStuck && !session._activeTask?.timedOut && (
                    <div className="mt-3 flex items-center justify-between gap-2 px-3 py-2 rounded-2xl"
                        style={{ background: 'rgba(202,138,4,0.04)', border: '1px solid rgba(202,138,4,0.12)' }}>
                        <span style={{ fontSize: 11, color: '#CA8A04' }}>Task may be stuck</span>
                        <button
                            onClick={(e) => { e.stopPropagation(); onStop?.(session); }}
                            className="font-medium px-2.5 py-0.5 rounded-full active:scale-95 transition-transform"
                            style={{ fontSize: 11, color: '#fff', background: '#CA8A04' }}
                        >
                            Stop
                        </button>
                    </div>
                )}

                {/* Failed — retry action */}
                {session._activeTask?.status === 'error' && session._activeTask?.recoverable && (
                    <div className="mt-3 flex items-center justify-between gap-2 px-3 py-2 rounded-2xl"
                        style={{ background: 'rgba(220,38,38,0.04)', border: '1px solid rgba(220,38,38,0.12)' }}>
                        <span style={{ fontSize: 11, color: '#DC2626' }}>Task failed</span>
                        <button
                            onClick={(e) => { e.stopPropagation(); onRetry?.(session); }}
                            className="font-medium px-2.5 py-0.5 rounded-full active:scale-95 transition-transform"
                            style={{ fontSize: 11, color: '#fff', background: '#DC2626' }}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Stale warning (legacy — 10min elapsed without task-level signals) */}
                {isStale && !session._activeTask && (
                    <div className="mt-3 flex items-center justify-between gap-2 px-3 py-2 rounded-2xl"
                        style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)' }}>
                        <div className="flex items-center gap-1.5">
                            <AlertCircle size={11} className="shrink-0" style={{ color: 'rgba(0,0,0,0.35)' }} />
                            <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.4)' }}>Session may have failed</span>
                        </div>
                        <button
                            onClick={(e) => { e.stopPropagation(); onDismiss?.(session); }}
                            className="font-medium px-2.5 py-0.5 rounded-full active:scale-95 transition-transform"
                            style={{ fontSize: 11, color: 'rgba(0,0,0,0.5)', background: 'rgba(0,0,0,0.05)' }}
                        >
                            Dismiss
                        </button>
                    </div>
                )}
            </div>
        </motion.div>
    );
}

// ── Cron Job section for Home page (active only) ──
function CronJobSection() {
    const [cronJobs, setCronJobs] = useState([]);
    const [heartbeat, setHeartbeat] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.getCronJobs()
            .then(data => {
                if (cancelled) return;
                // Only keep enabled/active cron jobs
                const active = (data?.cron_jobs || []).filter(j => j.enabled);
                setCronJobs(active);
                // Only show heartbeat if enabled
                const hb = data?.heartbeat;
                setHeartbeat(hb?.enabled ? hb : null);
                setLoaded(true);
            })
            .catch(() => setLoaded(true));
        return () => { cancelled = true; };
    }, []);

    if (!loaded || (cronJobs.length === 0 && !heartbeat)) return null;

    const formatSchedule = (cron) => {
        // Simple human-readable cron format
        const parts = cron.split(' ');
        if (parts.length < 5) return cron;
        const [min, hour, dom, mon, dow] = parts;
        if (dom === '*' && mon === '*' && dow === '*') return `Daily at ${hour}:${min.padStart(2, '0')}`;
        if (dom === '*' && mon === '*' && dow !== '*') {
            const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            return `${days[dow] || dow} at ${hour}:${min.padStart(2, '0')}`;
        }
        if (min.startsWith('*/')) return `Every ${min.slice(2)} min`;
        if (hour.startsWith('*/')) return `Every ${hour.slice(2)} hours`;
        return cron;
    };

    return (
        <div className="mb-5">
            <span
                className="font-semibold tracking-wide uppercase px-1 block mb-3"
                style={{ fontSize: 11, color: 'rgba(0,0,0,0.35)', letterSpacing: '0.06em' }}
            >
                Automations · {cronJobs.length + (heartbeat ? 1 : 0)}
            </span>

            <div
                style={{
                    borderRadius: 16,
                    background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                }}
            >
                {/* Heartbeat row */}
                {heartbeat && (
                    <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: cronJobs.length > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                        <span style={{ fontSize: 18 }}>💓</span>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium truncate" style={{ fontSize: 14, color: '#000' }}>Heartbeat</div>
                            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.4)' }}>
                                Every {Math.round(heartbeat.intervalMs / 60000)} min · {heartbeat.mode}
                            </div>
                        </div>
                        <div
                            className="px-2 py-0.5 rounded-full"
                            style={{ fontSize: 11, fontWeight: 500, background: 'rgba(34,197,94,0.1)', color: '#16a34a' }}
                        >
                            Active
                        </div>
                    </div>
                )}

                {/* Cron job rows */}
                {cronJobs.map((job, i) => (
                    <div
                        key={job.id}
                        className="flex items-center gap-3 px-4 py-3"
                        style={{ borderBottom: i < cronJobs.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}
                    >
                        <span style={{ fontSize: 18 }}>⏰</span>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium truncate" style={{ fontSize: 14, color: '#000' }}>{job.name}</div>
                            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.4)' }}>
                                {formatSchedule(job.schedule)}
                                {job.nextRun && ` · Next: ${new Date(job.nextRun).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                            </div>
                        </div>
                        <div
                            className="px-2 py-0.5 rounded-full"
                            style={{ fontSize: 11, fontWeight: 500, background: 'rgba(34,197,94,0.1)', color: '#16a34a' }}
                        >
                            Active
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Evolution section — shows recent evolution records (running, completed, failed) ──
const EVOLUTION_STATUS = {
    running: { color: '#7C3AED', bg: 'rgba(139,92,246,0.1)', label: 'Evolving', dot: true },
    completed: { color: '#059669', bg: 'rgba(52,211,153,0.1)', label: 'Done', dot: false },
    failed: { color: '#DC2626', bg: 'rgba(248,113,113,0.1)', label: 'Failed', dot: false },
    promoted: { color: '#2563EB', bg: 'rgba(96,165,250,0.1)', label: 'Promoted', dot: false },
};

function EvolvingSection({ onSelectEvolution }) {
    const [records, setRecords] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.authFetch('/api/nanoclaw/evolution/status')
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (cancelled || !data) return;
                // Show running + last 5 recent completed/failed
                const all = (data.history || []).sort((a, b) => b.timestamp - a.timestamp);
                const running = all.filter(r => r.status === 'running');
                const recent = all.filter(r => r.status !== 'running').slice(0, 5);
                setRecords([...running, ...recent]);
                setLoaded(true);
            })
            .catch(() => setLoaded(true));
        return () => { cancelled = true; };
    }, []);

    if (!loaded || records.length === 0) return null;

    const runningCount = records.filter(r => r.status === 'running').length;
    const sectionLabel = runningCount > 0 ? `Evolving · ${runningCount}` : 'Evolution';

    return (
        <div className="mb-5">
            <span
                className="font-semibold tracking-wide uppercase px-1 block mb-3"
                style={{ fontSize: 11, color: 'rgba(0,0,0,0.35)', letterSpacing: '0.06em' }}
            >
                {sectionLabel}
            </span>

            <div
                style={{
                    borderRadius: 16,
                    background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                }}
            >
                {records.map((record, i) => {
                    const cfg = EVOLUTION_STATUS[record.status] || EVOLUTION_STATUS.failed;
                    const date = new Date(record.timestamp);
                    const timeAgo = getTimeAgo(date);

                    return (
                        <div
                            key={`${record.skillSlug}-${record.timestamp}`}
                            className="flex items-center gap-3 px-4 py-3 cursor-pointer active:bg-black/[0.02] transition-colors"
                            style={{ borderBottom: i < records.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}
                            onClick={() => onSelectEvolution?.(record)}
                        >
                            <span style={{ fontSize: 18 }}>🧬</span>
                            <div className="flex-1 min-w-0">
                                <div className="font-medium truncate" style={{ fontSize: 14, color: '#000' }}>{record.skillSlug}</div>
                                <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.4)' }}>
                                    {record.status === 'completed' && record.newPassRate > 0
                                        ? `${record.oldPassRate}% → ${record.newPassRate}% · ${timeAgo}`
                                        : `${record.sessionCount || 0} sessions · ${timeAgo}`
                                    }
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                                {cfg.dot && (
                                    <motion.div
                                        className="w-1.5 h-1.5 rounded-full"
                                        style={{ background: cfg.color }}
                                        animate={{ opacity: [1, 0.3, 1] }}
                                        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                                    />
                                )}
                                <span
                                    className="px-2 py-0.5 rounded-full"
                                    style={{ fontSize: 11, fontWeight: 500, background: cfg.bg, color: cfg.color }}
                                >
                                    {cfg.label}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function getTimeAgo(date) {
    const diff = Date.now() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

// ─── Main component ─────────────────────────────────────────────────────────
export default function HistoryView({
    onBack, onOpenCamera, onSelectSession, onSelectEvolution, onProfileTap, onClearSessionCache, onOpenAllTasks,
    isAuthenticated, user, isHome, livekit, onNotification,
    nanoClaw,
    sseEvents = [], sseConnected = false,
}) {
    const { play } = useSound();
    const [liveSessions, setLiveSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const mountedRef = useRef(true);
    const longPressTimerRef = useRef(null);
    const longPressStartRef = useRef(null);

    // ── Home input bar state ──
    const [homeText, setHomeText] = useState('');
    const [homeSending, setHomeSending] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const suggestionsLoadedRef = useRef(false);

    // Fetch proactive suggestions on mount
    useEffect(() => {
        if (suggestionsLoadedRef.current || !isAuthenticated) return;
        suggestionsLoadedRef.current = true;
        api.getSuggestions()
            .then(resp => {
                if (resp?.suggestions?.length) setSuggestions(resp.suggestions);
            })
            .catch(() => { /* silent — fallback to empty */ });
    }, [isAuthenticated]);

    // Dispatch a text task from Home → create session → navigate to it
    const handleHomeDispatch = useCallback(async (promptText) => {
        if (!promptText?.trim() || homeSending) return;
        setHomeSending(true);
        play('session.send');
        try {
            const resp = await api.dispatchExec({ prompt: promptText.trim() });
            if (resp?.sessionId) {
                onSelectSession({
                    sessionId: resp.sessionId,
                    prompt: promptText.trim(),
                    fromHome: false,
                    needsPromptConfirm: false,
                });
            }
        } catch (e) {
            console.error('[Home] dispatch failed:', e);
        } finally {
            setHomeSending(false);
            setHomeText('');
        }
    }, [homeSending, play, onSelectSession]);

    // ── Preload all session photos into browser cache ──
    const allPhotoUrls = useMemo(() => {
        return liveSessions.flatMap(s => extractPhotos(s).map(p => p?.url || p));
    }, [liveSessions]);
    useImagePreloader(allPhotoUrls);

    // ── Long-press handlers ──
    const handlePointerDown = useCallback((session, e) => {
        longPressStartRef.current = { x: e.clientX, y: e.clientY };
        longPressTimerRef.current = setTimeout(() => {
            longPressTimerRef.current = null;
            navigator.vibrate?.(50);
            setDeleteTarget(session);
        }, 500);
    }, []);

    const handlePointerMove = useCallback((e) => {
        if (!longPressTimerRef.current || !longPressStartRef.current) return;
        const dx = e.clientX - longPressStartRef.current.x;
        const dy = e.clientY - longPressStartRef.current.y;
        if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    }, []);

    const handlePointerUp = useCallback(() => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    }, []);

    const confirmDeleteSession = useCallback(async () => {
        if (!deleteTarget) return;
        play('media.delete');
        const sessionId = deleteTarget.id;
        setLiveSessions(prev => prev.filter(t => t.id !== sessionId));
        setDeleteTarget(null);
        onClearSessionCache?.(sessionId);
        try { await api.deleteSession(sessionId); } catch (err) { console.error('Delete failed:', err); }
    }, [deleteTarget, play, onClearSessionCache]);

    // ── Fetch sessions ──
    const fetchSessions = useCallback(async (isInitial = false) => {
        const viUserId = api.getViUserId();
        if (!isAuthenticated && !localStorage.getItem('test-auth-token')) {
            if (isInitial) {
                setLiveSessions([]);
                setLoading(false);
            }
            return;
        }
        if (isInitial) setLoading(true);
        try {
            const sessions = await api.getSessions().catch(() => []);
            if (mountedRef.current) {
                const normalized = (sessions || []).map(s => {
                    // Extract HTML: prefer result_html, fall back to first card in result.cards
                    let html = s.result_html || '';
                    if (!html && s.result?.cards) {
                        const firstCard = Object.values(s.result.cards)[0];
                        if (firstCard?.data?.html) html = firstCard.data.html;
                    }
                    return {
                        id: s.id,
                        prompt: s.prompt || s.title || s.intention || '',
                        status: s.status === 'completed' || s.status === 'ended' ? 'complete'
                            : s.status === 'queued' || s.status === 'dispatched' || s.status === 'created' ? 'pending'
                                : s.status === 'failed' ? 'error'
                                    : s.status || 'pending',
                        result: s.result || null,
                        created_at: s.started_at || s.dispatched_at || s.created_at,
                        context: { ...s.context, progress_message: s.progress_message },
                        title: s.title,
                        result_html: html,
                        thumbnail_url: s.thumbnail_url || null,
                        result_summary: s.result_summary,
                        timeline: s.timeline || [],
                    };
                });
                normalized.sort((a, b) => {
                    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
                    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
                    return tb - ta;
                });
                setLiveSessions(normalized);
            }
        } catch (err) {
            console.error('Failed to fetch sessions:', err);
            if (mountedRef.current && isInitial) setLiveSessions([]);
        } finally {
            if (mountedRef.current && isInitial) setLoading(false);
        }
    }, [isAuthenticated]);

    // Initial fetch only (no polling — SSE handles live updates)
    useEffect(() => {
        mountedRef.current = true;
        fetchSessions(true);
        return () => {
            mountedRef.current = false;
        };
    }, [fetchSessions]);

    // Re-fetch when LiveKit sessionId becomes available
    const prevSessionIdRef = useRef(null);
    useEffect(() => {
        if (livekit?.sessionId && livekit.sessionId !== prevSessionIdRef.current) {
            prevSessionIdRef.current = livekit.sessionId;
            fetchSessions(liveSessions.length === 0);
        }
    }, [livekit?.sessionId, fetchSessions, liveSessions.length]);

    // LiveKit DataChannel task events — process ALL unprocessed events
    const lastProcessedTaskEventIdx = useRef(0);
    useEffect(() => {
        if (!livekit?.taskEvents || livekit.taskEvents.length === 0) return;
        const events = livekit.taskEvents;
        const startIdx = lastProcessedTaskEventIdx.current;
        if (startIdx >= events.length) return;
        lastProcessedTaskEventIdx.current = events.length;

        let needsFetch = false;
        for (let i = startIdx; i < events.length; i++) {
            const ev = events[i];
            if (!ev) continue;

            if (ev.type === 'task_started') {
                setLiveSessions(prev => {
                    if (prev.some(t => t.id === ev.task_id)) return prev;
                    return [{ id: ev.task_id, prompt: ev.description, status: 'pending', created_at: new Date().toISOString() }, ...prev];
                });
            } else if (ev.type === 'task_progress') {
                setLiveSessions(prev => prev.map(t => t.id === ev.task_id ? { ...t, status: 'progress' } : t));
            } else if (ev.type === 'task_result') {
                setLiveSessions(prev => prev.map(t =>
                    t.id === ev.task_id ? { ...t, status: ev.status || 'complete', result: ev.result } : t
                ));
                needsFetch = true;
            }
        }
        if (needsFetch) fetchSessions(false);
    }, [livekit?.taskEvents, fetchSessions]);

    // SSE real-time events — session_update drives active/historical transitions
    const lastSseEventRef = useRef(0);
    useEffect(() => {
        if (sseEvents.length === 0) return;
        const latest = sseEvents[sseEvents.length - 1];
        if (!latest || latest._ts <= lastSseEventRef.current) return;
        lastSseEventRef.current = latest._ts;

        if (latest.type === 'session_update') {
            const { session_id, status, prompt, progress_message, result_summary, media_urls } = latest;
            if (!session_id) return;

            setLiveSessions(prev => {
                const idx = prev.findIndex(t => t.id === session_id);

                // New session not in list — add as active if dispatched/pending/progress
                if (idx === -1) {
                    if (status === 'queued' || status === 'dispatched' || status === 'pending' || status === 'created' || status === 'progress' || status === 'processing') {
                        const context = {};
                        if (media_urls?.length > 0) context.photos = media_urls;
                        return [{
                            id: session_id,
                            prompt: prompt || '',
                            status: status === 'queued' || status === 'dispatched' || status === 'created' ? 'pending' : 'progress',
                            created_at: new Date().toISOString(),
                            context,
                        }, ...prev];
                    }
                    // Completed/failed session not in list — fetch full data from DB
                    fetchSessions(false);
                    return prev;
                }

                // Existing session — update status
                const updated = [...prev];
                const entry = { ...updated[idx] };
                if (status === 'queued' || status === 'dispatched' || status === 'pending' || status === 'created') {
                    entry.status = 'pending';
                } else if (status === 'progress' || status === 'processing') {
                    entry.status = 'progress';
                    if (progress_message) entry.context = { ...entry.context, progress_message };
                } else if (status === 'completed' || status === 'complete') {
                    entry.status = 'complete';
                    if (result_summary) entry.result = entry.result || { summary: result_summary };
                    // Re-fetch to get full persisted data for the completed session
                    fetchSessions(false);
                } else if (status === 'failed' || status === 'error') {
                    entry.status = 'error';
                    // Re-fetch to get full persisted data
                    fetchSessions(false);
                }
                updated[idx] = entry;
                return updated;
            });
            // Note: toast notifications for completed/failed are handled by App.jsx's handleSessionUpdate
        } else if (latest.type === 'memory_update') {
            onNotification?.({ type: 'memory_update', ...latest });
        }
    }, [sseEvents, fetchSessions, onNotification]);

    // ── Helpers ──
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        const now = new Date();
        const diff = now - d;
        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        return d.toLocaleDateString();
    };

    const handleSessionClick = (session) => {
        play('nav.forward');
        const photos = extractPhotos(session);
        const isHistorical = session.status === 'complete' || session.status === 'error';

        // For historical sessions, replay through sessionStore for consistent card state
        if (isHistorical && nanoClaw?.loadSession) {
            nanoClaw.loadSession({
                id: session.id,
                result: session.result || null,
                timeline: session.timeline || [],
            });
        }

        onSelectSession({
            title: getShortTitle(session.prompt),
            prompt: session.prompt || '',
            sessionId: session.id,
            fromHome: true,
            result: session.result || null,
            result_html: session.result_html || null,
            timeline: session.timeline || [],
            status: session.status,
            photos: photos.map(p => {
                const src = p?.url || p;
                const ext = src.split('.').pop()?.toLowerCase();
                const isVideo = ['mp4', 'webm', 'mov', 'avi'].includes(ext);
                return { type: isVideo ? 'video' : 'photo', src };
            }),
            context: session.context || null,
            created_at: session.created_at,
            _sessionLoaded: isHistorical, // signal that loadSession was already called
        });
    };

    // ── Derived session lists (must be before any useEffect that references them) ──
    // Enrich active sessions with task-level status from useSessionStore
    const storeTasks = nanoClaw?.tasks || {};
    const activeSessions = liveSessions
        .filter(t => t.status === 'pending' || t.status === 'progress')
        .map(session => {
            // Find the most recent active task for this session
            const activeTask = Object.values(storeTasks).find(
                t => t.sessionId === session.id && (t.status === 'loading' || t.status === 'streaming' || t.status === 'queued' || t.status === 'timeout' || t.status === 'error' || t.status === 'stopped')
            ) || Object.values(storeTasks).find(t => t.sessionId === session.id);
            return activeTask ? { ...session, _activeTask: activeTask } : session;
        });
    const completedSessions = liveSessions.filter(t => t.status === 'complete' || t.status === 'error');

    const calendarScrollRef = useRef(null);

    // ── Active sessions carousel ──
    const carouselRef = useRef(null);
    const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);

    // Track carousel scroll position for pagination dots
    const handleCarouselScroll = useCallback(() => {
        const el = carouselRef.current;
        if (!el || !el.children.length) return;
        const cardWidth = el.children[0]?.offsetWidth || 1;
        const idx = Math.round(el.scrollLeft / cardWidth);
        setActiveCarouselIndex(idx);
    }, []);

    // Snap carousel to valid position when active sessions count changes
    const prevActiveCountRef = useRef(0);
    useEffect(() => {
        const count = activeSessions.length;
        if (prevActiveCountRef.current !== count && carouselRef.current) {
            prevActiveCountRef.current = count;
            // Clamp index and snap smoothly
            const newIdx = Math.min(activeCarouselIndex, Math.max(0, count - 1));
            setActiveCarouselIndex(newIdx);
            requestAnimationFrame(() => {
                if (carouselRef.current && carouselRef.current.children[0]) {
                    const cardWidth = carouselRef.current.children[0].offsetWidth;
                    carouselRef.current.scrollTo({ left: newIdx * cardWidth, behavior: 'smooth' });
                }
            });
        }
    }, [activeSessions.length, activeCarouselIndex]);

    const formatTimestamp = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        return `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <motion.div
            initial={isHome ? false : { x: '-100%' }}
            animate={isHome ? false : { x: 0 }}
            exit={isHome ? { opacity: 0 } : { x: '-100%' }}
            transition={isHome ? undefined : IOS_SPRING}
            className="w-full h-full relative z-40 overflow-hidden"
            style={{ background: '#F2F2F7' }}
        >
            {/* ═══ Fixed layout — flex column ═══ */}
            <div className="w-full h-full flex flex-col">

                {/* ═══ Header — profile avatar + title ═══ */}
                <div className="safe-area-top flex items-center justify-between px-4 pt-2 pb-2" style={{ flexShrink: 0 }}>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => { play('nav.forward'); onProfileTap?.(); }}
                            className="w-9 h-9 rounded-full flex items-center justify-center active:scale-90 transition-transform"
                            style={{
                                background: '#000',
                            }}
                        >
                            {isAuthenticated && user?.displayName ? (
                                <span className="text-white font-semibold" style={{ fontSize: 13 }}>
                                    {user.displayName.charAt(0).toUpperCase()}
                                </span>
                            ) : (
                                <User size={16} strokeWidth={2} className="text-white" />
                            )}
                        </button>
                        <span className="font-semibold" style={{ fontSize: 17, color: '#000', letterSpacing: '-0.02em' }}>
                            Home
                        </span>
                    </div>
                    {/* spacer to balance layout */}
                    <div className="w-8 h-8" />
                </div>

                {/* ═══ Scrollable content area ═══ */}
                <div
                    ref={calendarScrollRef}
                    className="px-3"
                    style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 160 }}
                >

                    {/* Loading */}
                    {loading && (
                        <div className="flex items-center justify-center py-14">
                            <Loader2 size={20} className="animate-spin" style={{ color: 'rgba(0,0,0,0.2)' }} />
                        </div>
                    )}

                    {/* ═══ Scheduled Tasks + Evolving (always visible, auth-gated to avoid 401s) ═══ */}
                    {!loading && isAuthenticated && (
                        <>
                            <CronJobSection />
                            <EvolvingSection onSelectEvolution={onSelectEvolution} />
                        </>
                    )}

                    {!loading && liveSessions.length > 0 && (
                        <>
                            {/* ═══ Active sessions — horizontal carousel ═══ */}
                            <AnimatePresence>
                                {activeSessions.length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="mb-5"
                                    >
                                        {/* Label */}
                                        <span
                                            className="font-semibold tracking-wide uppercase px-1 block mb-3"
                                            style={{ fontSize: 11, color: 'rgba(0,0,0,0.35)', letterSpacing: '0.06em' }}
                                        >
                                            Active · {activeSessions.length}
                                        </span>

                                        {/* Carousel container */}
                                        <div
                                            ref={carouselRef}
                                            onScroll={handleCarouselScroll}
                                            className="flex gap-3 no-scrollbar"
                                            style={{
                                                overflowX: 'auto',
                                                scrollSnapType: 'x mandatory',
                                                WebkitOverflowScrolling: 'touch',
                                            }}
                                        >
                                            {activeSessions.map(session => (
                                                <div
                                                    key={session.id}
                                                    style={{
                                                        flex: '0 0 100%',
                                                        scrollSnapAlign: 'start',
                                                        minWidth: 0,
                                                    }}
                                                    onPointerDown={e => handlePointerDown(session, e)}
                                                    onPointerMove={handlePointerMove}
                                                    onPointerUp={handlePointerUp}
                                                    onPointerLeave={handlePointerUp}
                                                >
                                                    <ActiveSessionCard
                                                        session={session}
                                                        onClick={() => handleSessionClick(session)}
                                                        onDismiss={s => {
                                                            play('media.delete');
                                                            setLiveSessions(prev => prev.filter(item => item.id !== s.id));
                                                            onClearSessionCache?.(s.id);
                                                            api.deleteSession(s.id).catch(err => console.error('Delete failed:', err));
                                                        }}
                                                        onRetry={s => {
                                                            console.log(`[UI:history] retrying session ${s.id}`);
                                                            api.dispatchExec({
                                                                prompt: s.prompt,
                                                                sessionId: s.id,
                                                                mediaUrls: extractPhotos(s).map(p => p?.url || p),
                                                            }).catch(err => console.error('Retry failed:', err));
                                                        }}
                                                        onStop={s => {
                                                            const task = s._activeTask;
                                                            if (task?.taskId) {
                                                                console.log(`[UI:history] stopping task ${task.taskId} in session ${s.id}`);
                                                                api.stopSession(s.id, task.taskId).catch(err => console.error('Stop failed:', err));
                                                            }
                                                        }}
                                                        formatDate={formatDate}
                                                    />
                                                </div>
                                            ))}
                                        </div>

                                        {/* Pagination dots */}
                                        {activeSessions.length > 1 && (
                                            <div className="flex items-center justify-center gap-1.5 mt-3">
                                                {activeSessions.map((s, i) => (
                                                    <div
                                                        key={s.id}
                                                        style={{
                                                            width: i === activeCarouselIndex ? 16 : 6,
                                                            height: 6,
                                                            borderRadius: 3,
                                                            background: i === activeCarouselIndex ? '#000' : 'rgba(0,0,0,0.12)',
                                                            transition: 'all 0.25s ease',
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* ═══ Completed tasks — dual-column waterfall ═══ */}
                            {completedSessions.length > 0 && (() => {
                                const leftCol = completedSessions.filter((_, i) => i % 2 === 0);
                                const rightCol = completedSessions.filter((_, i) => i % 2 === 1);
                                const renderCard = (session) => {
                                    const photos = extractPhotos(session);
                                    const heroImg = photos[0]?.url || photos[0] || null;
                                    const previewImg = heroImg; // TODO: switch to session.thumbnail_url for sketch previews
                                    return (
                                        <div
                                            key={session.id}
                                            onClick={() => handleSessionClick(session)}
                                            onPointerDown={e => handlePointerDown(session, e)}
                                            onPointerMove={handlePointerMove}
                                            onPointerUp={handlePointerUp}
                                            onPointerLeave={handlePointerUp}
                                            className="cursor-pointer active:scale-[0.97] transition-transform"
                                            style={{
                                                borderRadius: 12,
                                                background: '#fff',
                                                overflow: 'hidden',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04)',
                                            }}
                                        >
                                            {/* Visual preview — sketch thumbnail or original photo */}
                                            {previewImg ? (
                                                <img
                                                    src={getCachedUrl(previewImg) || previewImg}
                                                    alt=""
                                                    style={{
                                                        width: '100%',
                                                        aspectRatio: '4 / 3',
                                                        objectFit: 'cover',
                                                        display: 'block',
                                                        background: '#fff',
                                                    }}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.style.aspectRatio = 'unset';
                                                        e.target.style.height = '100px';
                                                        e.target.style.objectFit = 'contain';
                                                        e.target.style.padding = '24px';
                                                        e.target.style.background = '#F2F2F7';
                                                        e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='1.5'%3E%3Crect x='3' y='3' width='18' height='18' rx='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpath d='m21 15-5-5L5 21'/%3E%3C/svg%3E";
                                                    }}
                                                />
                                            ) : (
                                                <div style={{
                                                    height: 100,
                                                    background: '#F8F8FA',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}>
                                                    <Eye size={24} strokeWidth={1.2} style={{ color: 'rgba(0,0,0,0.12)' }} />
                                                </div>
                                            )}
                                            <div style={{ padding: '8px 10px 10px' }}>
                                                <p
                                                    style={{
                                                        fontSize: 13,
                                                        fontWeight: 500,
                                                        color: '#000',
                                                        lineHeight: 1.4,
                                                        letterSpacing: '-0.01em',
                                                        margin: 0,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {getShortTitle(session.prompt)}
                                                </p>
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        color: 'rgba(0,0,0,0.3)',
                                                        marginTop: 3,
                                                        display: 'block',
                                                    }}
                                                >
                                                    {formatDate(session.created_at)}
                                                </span>
                                                {session.status === 'error' && (
                                                    <div className="flex items-center gap-1 mt-1">
                                                        <AlertCircle size={11} style={{ color: '#ef4444' }} />
                                                        <span style={{ fontSize: 11, color: '#ef4444' }}>Failed</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                };
                                return (
                                    <div className="flex gap-2.5">
                                        <div className="flex-1 flex flex-col gap-2.5">
                                            {leftCol.map(renderCard)}
                                        </div>
                                        <div className="flex-1 flex flex-col gap-2.5">
                                            {rightCol.map(renderCard)}
                                        </div>
                                    </div>
                                );
                            })()}
                        </>
                    )}

                    {/* ═══ Empty state ═══ */}
                    {!loading && liveSessions.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="flex flex-col items-center justify-center px-6 pt-10 pb-12"
                        >
                            <motion.div
                                initial={{ opacity: 0, scale: 0.5 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.3, ...IOS_SPRING }}
                                className="mb-6 w-20 h-20 rounded-full flex items-center justify-center"
                                style={{ background: 'rgba(0,0,0,0.04)' }}
                            >
                                <Eye size={32} strokeWidth={1.2} style={{ color: 'rgba(0,0,0,0.18)' }} />
                            </motion.div>

                            {!api.getViUserId() ? (
                                <>
                                    <motion.p
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.45 }}
                                        className="font-semibold text-center leading-snug tracking-tight mb-1"
                                        style={{ fontSize: 17, color: 'rgba(0,0,0,0.65)' }}
                                    >
                                        Connecting…
                                    </motion.p>
                                    <p style={{ fontSize: 13, color: 'rgba(0,0,0,0.3)' }} className="text-center">
                                        Your sessions will appear here
                                    </p>
                                </>
                            ) : (
                                <>
                                    <motion.p
                                        initial={{ opacity: 0, y: 14 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.45, duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
                                        className="font-bold text-center leading-snug tracking-tight max-w-[240px] mb-2"
                                        style={{ fontSize: 20, color: '#000' }}
                                    >
                                        The AI That Sees For You
                                    </motion.p>
                                    <motion.p
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.55, duration: 0.5 }}
                                        className="text-center max-w-[220px] mb-8"
                                        style={{ fontSize: 13, color: 'rgba(0,0,0,0.35)', lineHeight: 1.5 }}
                                    >
                                        Point your camera at something to begin your first session
                                    </motion.p>
                                    <motion.button
                                        onClick={() => { play('nav.forward'); onOpenCamera?.(); }}
                                        initial={{ opacity: 0, y: 12 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.65, ...IOS_SPRING }}
                                        whileTap={{ scale: 0.92 }}
                                        whileHover={{ scale: 1.03 }}
                                        className="flex items-center gap-2.5 px-6 py-3 rounded-full"
                                        style={{
                                            background: '#000',
                                            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                                        }}
                                    >
                                        <Camera size={16} strokeWidth={2} className="text-white" />
                                        <span className="font-semibold text-white" style={{ fontSize: 14 }}>
                                            Start exploring
                                        </span>
                                    </motion.button>
                                </>
                            )}
                        </motion.div>
                    )}
                </div>
            </div>

            {/* ═══ Bottom fade ═══ */}
            <div
                className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none z-40"
                style={{ background: 'linear-gradient(to top, #F2F2F7 40%, transparent)' }}
            />

            {/* ═══ Bottom Input Area ═══ */}
            <div className="absolute bottom-0 left-0 right-0 z-50"
                style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}>

                {/* Suggestion chips — horizontal scroll */}
                {suggestions.length > 0 && (
                    <div className="px-3 pb-2">
                        <div className="flex gap-2 overflow-x-auto no-scrollbar">
                            {suggestions.map((s, i) => (
                                <motion.button
                                    key={i}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handleHomeDispatch(`${s.text}`)}
                                    className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full transition-all active:scale-95"
                                    style={{
                                        background: 'rgba(255,255,255,0.85)',
                                        backdropFilter: 'blur(20px)',
                                        WebkitBackdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(0,0,0,0.06)',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                                    }}
                                >
                                    <span style={{ fontSize: 14 }}>{s.icon}</span>
                                    <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.55)', whiteSpace: 'nowrap' }}>{s.text}</span>
                                </motion.button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Floating input pill — matches session input */}
                <div className="px-3 py-1.5">
                    <motion.div
                        className="relative overflow-hidden"
                        style={{
                            borderRadius: 28,
                            background: 'rgba(255,255,255,0.85)',
                            backdropFilter: 'blur(40px)',
                            WebkitBackdropFilter: 'blur(40px)',
                            boxShadow: '0 0 0 1px rgba(0,0,0,0.06), 0 4px 24px rgba(0,0,0,0.06)',
                        }}
                    >
                        <div className="relative flex items-center gap-1.5 px-2 py-1.5">
                            {/* Camera button */}
                            <button
                                onClick={() => {
                                    livekit?.ensureAudioContext?.();
                                    play('nav.forward');
                                    onOpenCamera ? onOpenCamera() : onBack?.();
                                }}
                                className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center transition-all"
                                style={{
                                    background: 'rgba(0,0,0,0.04)',
                                    color: 'rgba(0,0,0,0.25)',
                                }}
                            >
                                <Camera size={18} strokeWidth={2} />
                            </button>

                            {/* Mic toggle */}
                            <button
                                onClick={() => {
                                    livekit?.ensureAudioContext?.();
                                    play(livekit?.isMicEnabled ? 'mic.off' : 'mic.on');
                                    livekit?.toggleMic?.();
                                }}
                                className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center transition-all"
                                style={{
                                    background: livekit?.isMicEnabled ? 'rgba(0,0,0,0.08)' : 'transparent',
                                    color: livekit?.isMicEnabled ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.2)',
                                }}
                            >
                                {livekit?.isMicEnabled
                                    ? <Mic size={15} strokeWidth={2} />
                                    : <MicOff size={15} strokeWidth={2} />
                                }
                            </button>

                            {/* Text input */}
                            <input
                                type="text"
                                value={homeText}
                                onChange={e => setHomeText(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleHomeDispatch(homeText); } }}
                                placeholder="Ask anything..."
                                className="flex-1 bg-transparent outline-none min-w-0"
                                style={{
                                    fontSize: 15,
                                    fontWeight: 400,
                                    color: '#000',
                                    letterSpacing: '-0.01em',
                                }}
                            />

                            {/* Send button */}
                            <button
                                onClick={() => handleHomeDispatch(homeText)}
                                disabled={!homeText.trim() || homeSending}
                                className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center transition-all duration-200"
                                style={{
                                    background: homeText.trim() ? '#000' : 'rgba(0,0,0,0.08)',
                                    color: homeText.trim() ? '#fff' : 'rgba(0,0,0,0.3)',
                                    boxShadow: homeText.trim() ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
                                }}
                            >
                                {homeSending
                                    ? <Loader2 size={16} className="animate-spin" />
                                    : <ArrowUp size={18} strokeWidth={2.5} />
                                }
                            </button>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* ═══ Delete confirmation bottom sheet ═══ */}
            <AnimatePresence>
                {deleteTarget && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] flex items-end justify-center"
                        style={{ background: 'rgba(0,0,0,0.25)' }}
                        onClick={() => setDeleteTarget(null)}
                    >
                        <motion.div
                            initial={{ y: 100 }}
                            animate={{ y: 0 }}
                            exit={{ y: 100 }}
                            transition={IOS_SPRING}
                            onClick={e => e.stopPropagation()}
                            className="mb-8 overflow-hidden"
                            style={{
                                width: 'calc(100vw - 32px)',
                                maxWidth: 360,
                                borderRadius: 20,
                                background: '#fff',
                                boxShadow: '0 -4px 40px rgba(0,0,0,0.12)',
                            }}
                        >
                            <div className="p-5">
                                <p className="font-semibold mb-3 text-center" style={{ fontSize: 15, color: '#000' }}>
                                    Delete this session?
                                </p>
                                <div
                                    className="flex items-center gap-3 rounded-2xl p-3"
                                    style={{ background: 'rgba(0,0,0,0.03)' }}
                                >
                                    {(() => {
                                        const photos = extractPhotos(deleteTarget);
                                        const delImg = photos[0]?.url || photos[0];
                                        return delImg ? (
                                            <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0"
                                                style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
                                                <img src={getCachedUrl(delImg) || delImg} alt="" className="w-full h-full object-cover" />
                                            </div>
                                        ) : null;
                                    })()}
                                    <p className="line-clamp-2 leading-snug" style={{ fontSize: 13, color: 'rgba(0,0,0,0.5)' }}>
                                        {getShortTitle(deleteTarget.prompt)}
                                    </p>
                                </div>
                            </div>
                            <div className="flex" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                                <button
                                    onClick={() => setDeleteTarget(null)}
                                    className="flex-1 py-3.5 font-medium hover:bg-black/[0.02] transition-colors"
                                    style={{ fontSize: 14, color: 'rgba(0,0,0,0.4)' }}
                                >
                                    Cancel
                                </button>
                                <div style={{ width: 1, background: 'rgba(0,0,0,0.06)' }} />
                                <button
                                    onClick={confirmDeleteSession}
                                    className="flex-1 py-3.5 font-semibold hover:bg-black/[0.02] transition-colors"
                                    style={{ fontSize: 14, color: '#000' }}
                                >
                                    Delete
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
